import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { IPermissionDef, ScopeEntry } from '@nexa/auth';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { roleLabel } from '@/lib/access/labels';
import { scopeChips } from '@/lib/team/scope';
import type { ITeamMemberRole, MemberStatus } from '@/lib/team/types';

// Small presentational pieces shared by the access screens (users-roles-permissions R6):
// role chips (system vs custom), scope chips, the member status badge and the §4 flags.

/** Roles as chips: system roles carry a lock, custom roles the accent. */
export function RoleChips({ roles }: { roles: readonly ITeamMemberRole[] }) {
  const { t } = useTranslation('admin');
  if (roles.length === 0) {
    return (
      <Badge variant="muted" size="sm" data-testid="role-chip-none">
        {t('users.roles.none')}
      </Badge>
    );
  }
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <Badge
          key={r.key}
          variant={r.system ? 'secondary' : 'info'}
          size="sm"
          title={r.system ? t('roles.badges.system') : t('roles.badges.custom')}
          data-testid="role-chip"
        >
          {r.system ? <Lock className="h-3 w-3" aria-hidden /> : null}
          {roleLabel(t, r)}
        </Badge>
      ))}
    </span>
  );
}

/** Scope as chips — "Toda la aerolínea", "Brasil", "GRU · SCL". */
export function ScopeChips({ scopes }: { scopes: readonly ScopeEntry[] }) {
  const { t, i18n } = useTranslation('admin');
  const chips = scopeChips(t, scopes, i18n.language);
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <Badge
          key={c.id}
          variant={c.tone === 'all' ? 'success' : c.tone === 'none' ? 'warning' : 'neutral'}
          size="sm"
          title={c.title}
          data-testid="scope-chip"
        >
          {c.label}
        </Badge>
      ))}
    </span>
  );
}

const STATUS_VARIANT: Record<MemberStatus, BadgeVariant> = {
  active: 'success',
  pending: 'warning',
  suspended: 'error',
};

export function MemberStatusBadge({ status }: { status: MemberStatus }) {
  const { t } = useTranslation('admin');
  return (
    <Badge variant={STATUS_VARIANT[status]} size="sm" data-testid="member-status">
      {t(`users.status.${status}`)}
    </Badge>
  );
}

/** The §4 flags of one permission: 💰 dinero · 🔒 datos personales · ⚠ alto impacto · [T]. */
export function PermissionFlags({ def }: { def: IPermissionDef }) {
  const { t } = useTranslation('admin');
  return (
    <span className="inline-flex flex-wrap gap-1">
      {def.flags.money ? (
        <Badge variant="warning" size="sm" title={t('roles.flags.moneyHint')}>
          <span aria-hidden>💰</span>
          {t('roles.flags.money')}
        </Badge>
      ) : null}
      {def.flags.pii ? (
        <Badge variant="info" size="sm" title={t('roles.flags.piiHint')}>
          <span aria-hidden>🔒</span>
          {t('roles.flags.pii')}
        </Badge>
      ) : null}
      {def.flags.highImpact ? (
        <Badge variant="error" size="sm" title={t('roles.flags.highImpactHint')}>
          <span aria-hidden>⚠</span>
          {t('roles.flags.highImpact')}
        </Badge>
      ) : null}
      {def.scope === 'tenant' ? (
        <Badge variant="muted" size="sm" title={t('roles.flags.tenantHint')}>
          {t('roles.flags.tenant')}
        </Badge>
      ) : null}
    </span>
  );
}
